import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import { enqueueOp, newId, nowIso, selectAll, withLocalWrite } from "./base.js";

/**
 * Player rows are polymorphic by owner: in v2 every player is
 * `ownerKind="team"` with `ownerId=teamId`. The owner shape is plumbed
 * through so future variants (loan pool, scouting list, …) can land
 * without a schema change.
 */

export interface PlayerRow {
  id: string;
  org_id: string;
  rev: number | null;
  updated_at: string;
  deleted_at: string | null;
  client_id: string | null;
  owner_kind: string;
  owner_id: string;
  name: string;
  image_ref: string | null;
  position: string | null;
  foot: string | null;
  shirt_number: number | null;
  email: string | null;
  notes: string | null;
  stats_json: string;
  [key: string]: string | number | null;
}

export interface PlayerStats {
  stamina?: number | null;
  gameIntelligence?: number | null;
  teamSpirit?: number | null;
  technique?: number | null;
}

export function parseStats(json: string): PlayerStats {
  try {
    return JSON.parse(json) as PlayerStats;
  } catch {
    return {};
  }
}

/** List players for one owner (always `kind="team"` in v2, but kept generic). */
export async function listPlayers(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  ownerKind: OwnerKind = "team",
): Promise<PlayerRow[]> {
  return selectAll<PlayerRow>(
    db,
    `SELECT * FROM player
       WHERE org_id = ? AND owner_kind = ? AND owner_id = ? AND deleted_at IS NULL
       ORDER BY shirt_number IS NULL, shirt_number ASC, name ASC`,
    [orgId, ownerKind, ownerId],
  );
}

export interface CreatePlayerInput {
  orgId: string;
  ownerId: string;
  ownerKind?: OwnerKind;
  name: string;
  position?: string | null;
  shirtNumber?: number | null;
  foot?: string | null;
  email?: string | null;
  notes?: string | null;
  imageRef?: string | null;
  stats?: PlayerStats;
}

export async function createPlayer(db: LocalDb, input: CreatePlayerInput): Promise<PlayerRow> {
  const id = newId();
  const updatedAt = nowIso();
  const ownerKind = input.ownerKind ?? "team";
  const stats = input.stats ?? {};
  const statsJson = JSON.stringify(stats);

  const row: PlayerRow = {
    id,
    org_id: input.orgId,
    rev: null,
    updated_at: updatedAt,
    deleted_at: null,
    client_id: null,
    owner_kind: ownerKind,
    owner_id: input.ownerId,
    name: input.name,
    image_ref: input.imageRef ?? null,
    position: input.position ?? null,
    foot: input.foot ?? null,
    shirt_number: input.shirtNumber ?? null,
    email: input.email ?? null,
    notes: input.notes ?? null,
    stats_json: statsJson,
  };

  await withLocalWrite(db, async (tx) => {
    await tx.exec(
      `INSERT INTO player
         (id, org_id, rev, updated_at, deleted_at, client_id, owner_kind, owner_id, name, image_ref, position, foot, shirt_number, email, notes, stats_json)
         VALUES (?, ?, NULL, ?, NULL, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        input.orgId,
        updatedAt,
        ownerKind,
        input.ownerId,
        input.name,
        row.image_ref,
        row.position,
        row.foot,
        row.shirt_number,
        row.email,
        row.notes,
        statsJson,
      ],
    );
    await enqueueOp(tx, {
      entity: "player",
      kind: "upsert",
      payload: {
        id,
        orgId: input.orgId,
        ownerKind,
        ownerId: input.ownerId,
        name: input.name,
        imageRef: row.image_ref,
        position: row.position,
        foot: row.foot,
        shirtNumber: row.shirt_number,
        email: row.email,
        notes: row.notes,
        statsJson,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: null,
    });
  });

  return row;
}

export interface UpdatePlayerInput {
  id: string;
  orgId: string;
  name: string;
  position: string | null;
  foot: string | null;
  shirtNumber: number | null;
  email: string | null;
  notes: string | null;
  imageRef: string | null;
  stats: PlayerStats;
}

export async function updatePlayer(db: LocalDb, input: UpdatePlayerInput): Promise<void> {
  const updatedAt = nowIso();
  const statsJson = JSON.stringify(input.stats);

  await withLocalWrite(db, async (tx) => {
    const existing = (
      await tx.query<PlayerRow>(`SELECT * FROM player WHERE id = ? LIMIT 1`, [input.id])
    )[0];
    if (!existing) throw new Error(`player ${input.id} not found`);

    await tx.exec(
      `UPDATE player
         SET name = ?, image_ref = ?, position = ?, foot = ?,
             shirt_number = ?, email = ?, notes = ?, stats_json = ?,
             updated_at = ?
         WHERE id = ?`,
      [
        input.name,
        input.imageRef,
        input.position,
        input.foot,
        input.shirtNumber,
        input.email,
        input.notes,
        statsJson,
        updatedAt,
        input.id,
      ],
    );
    await enqueueOp(tx, {
      entity: "player",
      kind: "upsert",
      payload: {
        id: input.id,
        orgId: existing.org_id,
        ownerKind: existing.owner_kind,
        ownerId: existing.owner_id,
        name: input.name,
        imageRef: input.imageRef,
        position: input.position,
        foot: input.foot,
        shirtNumber: input.shirtNumber,
        email: input.email,
        notes: input.notes,
        statsJson,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: existing.rev,
    });
  });
}

export interface UpdatePlayerStatsInput {
  id: string;
  orgId: string;
  stats: PlayerStats;
}

export async function updatePlayerStats(db: LocalDb, input: UpdatePlayerStatsInput): Promise<void> {
  const updatedAt = nowIso();
  const statsJson = JSON.stringify(input.stats);

  await withLocalWrite(db, async (tx) => {
    const existing = (
      await tx.query<PlayerRow>(`SELECT * FROM player WHERE id = ? LIMIT 1`, [input.id])
    )[0];
    if (!existing) throw new Error(`player ${input.id} not found`);

    await tx.exec(`UPDATE player SET stats_json = ?, updated_at = ? WHERE id = ?`, [
      statsJson,
      updatedAt,
      input.id,
    ]);
    await enqueueOp(tx, {
      entity: "player",
      kind: "upsert",
      payload: {
        id: input.id,
        orgId: existing.org_id,
        ownerKind: existing.owner_kind,
        ownerId: existing.owner_id,
        name: existing.name,
        imageRef: existing.image_ref,
        position: existing.position,
        foot: existing.foot,
        shirtNumber: existing.shirt_number,
        email: existing.email,
        notes: existing.notes,
        statsJson,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: existing.rev,
    });
  });
}

export async function deletePlayer(
  db: LocalDb,
  args: { id: string; orgId: string },
): Promise<void> {
  const updatedAt = nowIso();
  await withLocalWrite(db, async (tx) => {
    await tx.exec(`UPDATE player SET deleted_at = ?, updated_at = ? WHERE id = ? AND org_id = ?`, [
      updatedAt,
      updatedAt,
      args.id,
      args.orgId,
    ]);
    await enqueueOp(tx, {
      entity: "player",
      kind: "delete",
      payload: { id: args.id },
      updatedAt,
      baseRev: null,
    });
  });
}
